import { useState, useRef } from 'react';
import { connect } from 'react-redux';

const PushButton = ({ 
  text,
  textColor,
  bgColor,
  hoverBgColor = '',
  borderColor = '',
  children,
  onClick,
  dropdownButton = false,
  dropdownOpen = false,
  isInline = false,
  disabled = false,
  id = '',
  title = '',
  className = '',
  isLoading = false,
  onMouseEnter = ()=>{},
  onMouseLeave = ()=>{},
}) => {

  const rippleDiameter = 400;
  const MOVE_THRESHOLD = 400;

  const containerRef = useRef(null);
  const rippleRef = useRef(null);

  // starting pointer position
  const pointerStartRef = useRef({ x: 0, y: 0 });
  const isRippleActiveRef = useRef(false);

  const getClientPosition = (event) => {
    if (event.touches && event.touches.length > 0) {
      return {
        clientX: event.touches[0].clientX,
        clientY: event.touches[0].clientY,
      };
    }

    return {
      clientX: event.clientX,
      clientY: event.clientY,
    };
  };

  const showRipple = (event) => {
    if(isLoading) return;

    const container = containerRef.current;
    const ripple = rippleRef.current;
    if (!container || !ripple) return;

    const rect = container.getBoundingClientRect();
    const { clientX, clientY } = getClientPosition(event);

    const x = clientX - rect.left;
    const y = clientY - rect.top;

    pointerStartRef.current = { x, y };
    isRippleActiveRef.current = true;

    // Reset ripple
    ripple.style.width = `${rippleDiameter}px`;
    ripple.style.height = `${rippleDiameter}px`;
    ripple.style.left = `${x - rippleDiameter * 0.5}px`;
    ripple.style.top = `${y - rippleDiameter * 0.5}px`;
    ripple.style.transition = 'none';
    ripple.style.transform = 'scale(0.3)';
    ripple.style.opacity = '0.1';
    ripple.style.display = 'block';

    // Force reflow
    ripple.getBoundingClientRect();

    // Animate ripple
    ripple.style.transition = 'transform 1s ease-out, opacity 1s ease-out';
    ripple.style.transform = 'scale(1)';
    ripple.style.opacity = '0.2';
  };

  const hideRipple = (hideFast) => {
    const ripple = rippleRef.current;
    if (!ripple || !isRippleActiveRef.current) return;

    isRippleActiveRef.current = false;

    if (hideFast === true) {
      ripple.style.transition = 'transform 100ms ease-out, opacity 100ms ease-out';
    }
    ripple.style.opacity = '0';

    setTimeout(() => {
      if(isRippleActiveRef.current) return;
      //ripple.style.display = 'none';
    }, 300);
  };

  const handleMove = (event) => {
    if (!isRippleActiveRef.current) return;

    const container = containerRef.current;
    if (!container) return;

    const rect = container.getBoundingClientRect();
    const { clientX, clientY } = getClientPosition(event);

    const x = clientX - rect.left;
    const y = clientY - rect.top;

    const dx = x - pointerStartRef.current.x;
    const dy = y - pointerStartRef.current.y;

    const distance = Math.sqrt(dx * dx + dy * dy);

    if (distance > MOVE_THRESHOLD) {
      hideRipple(true);
    }
  };

  return (
    <button
      ref={containerRef}
      {...(id === '' ? {} : {id: id})}
      {...(title === '' ? {} : {title: title})}
      className={`
        group/pushbutton
        ${!isInline ? 'w-full' : ''} h-full
        relative z-[0] overflow-hidden 
        flex justify-center items-center gap-x-1.5
        ${dropdownButton === true && dropdownOpen === true ? 'rounded-t-lg' : 'rounded-lg'}
        select-none text-black
        ${borderColor.includes('border-') ? `border ${borderColor}` : 'border border-transparent hover:border-stone-800'}
        ${disabled === true ? 'cursor-not-allowed opacity-30' : 'cursor-pointer'}
        ${dropdownButton !== true && disabled !== true ? 'hover:scale-[1.03] active:scale-[0.97]' : ''}
        ${className}
      `}
      onMouseDown={showRipple}
      onMouseUp={hideRipple}
      onMouseEnter={onMouseEnter}
      onMouseLeave={() => {
        hideRipple();
        try { onMouseLeave(); } catch(error) {}
      }}
      onMouseMove={handleMove}
      onTouchStart={showRipple}
      onTouchEnd={hideRipple}
      onTouchCancel={hideRipple}
      onTouchMove={handleMove}
      onClick={onClick}
      disabled={disabled || isLoading}
    >
      {/* Normal background, use bgColor as tailwindcss style if it includes the bg- prefix */}
      <div
        className={`absolute left-0 top-0 w-full h-full z-[0] pointer-events-none ${bgColor.includes('bg-') ? bgColor : ''}`}
        style={{
          ...(bgColor.includes('bg-') ? {} : {backgroundColor: `${bgColor}`}),
        }}
      />

      {/* Hover background, use hoverBgColor as tailwindcss style if it includes the bg- prefix */}
      <div
        className={`absolute left-0 top-0 w-full h-full z-[1] pointer-events-none opacity-[0] group-hover/pushbutton:opacity-[1] transition-all duration-150 ${hoverBgColor.includes('bg-') ? hoverBgColor : ''}`}
        style={{
          ...(hoverBgColor.includes('bg-') ? {} : {backgroundColor: `${hoverBgColor}`}),
        }}
      />

      {/* Ripple */}
      <div
        ref={rippleRef}
        className="absolute z-[2] pointer-events-none bg-black rounded-full hidden"
      />

      {/* Loading Overlay */}
      <div
        className={`absolute inset-0 z-[4] flex items-center justify-center 
          bg-black/20 backdrop-blur-[1px] 
          transition-opacity duration-200
          ${isLoading ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
      >
        <div className="w-5 h-5 border border-white border-t-transparent rounded-full animate-spin" />
      </div>

      {/* Button text */}
      <div 
        className={`flex justify-center ${!isInline ? 'w-full' : ''} h-full font-bold z-[3] whitespace-nowrap p-[10px]`}
        style={{
          color: `${textColor}`,
        }}
      >
        {children}
      </div>

    </button>
  );
};

// Redux hookup
const mapStateToProps = (state) => ({});

export default connect(mapStateToProps, {})(PushButton);
